import type {
  QuestionnaireBuildFailureCode,
  QuestionnaireHttpOperation,
  QuestionnaireOperationsErrorBody,
  QuestionnaireRefusalParams,
  QuestionnaireRePlanFailureCode,
} from "@digital-twins/shared-types";
import { groupRefusalLines } from "@digital-twins/shared-types";

import {
  type IssueCopyContext,
  formatIssueCopy,
  formatReferenceLine,
  requestFailureLine,
  SAVE_CONFLICT_COPY,
  SAVE_FAILURE_FALLBACK,
  ruleCopy,
  transportFailureCopy,
} from "./questionnaireIssueCopy";

const QUESTIONNAIRE_BUNDLE_CONFLICT_CODE = "questionnaire_bundle_conflict";
const QUESTIONNAIRE_EDIT_REVISION_CONFLICT_CODE =
  "questionnaire_edit_revision_conflict";

const QUESTIONNAIRE_BUNDLE_CONFLICT_USER_MESSAGE =
  "The questionnaire changed while being prepared for review. Wait a moment, then try Review again.";

export const QUESTIONNAIRE_EDIT_REVISION_CONFLICT_USER_MESSAGE =
  "This questionnaire changed in another tab. Your change was not applied.";

function conflictResponseOf(error: unknown): {
  status?: number;
  data?: { code?: string };
} | undefined {
  return (
    error as {
      response?: { status?: number; data?: { code?: string } };
    }
  )?.response;
}

export function isQuestionnaireBundleConflict(error: unknown): boolean {
  const response = conflictResponseOf(error);

  return (
    response?.status === 409 &&
    response?.data?.code === QUESTIONNAIRE_BUNDLE_CONFLICT_CODE
  );
}

export function isQuestionnaireEditRevisionConflict(error: unknown): boolean {
  const response = conflictResponseOf(error);

  return (
    response?.status === 409 &&
    response?.data?.code === QUESTIONNAIRE_EDIT_REVISION_CONFLICT_CODE
  );
}

/**
 * Every 409 that means "the stored draft moved past the revision this write
 * started from": a lost compare-and-swap, a stale `expectedRevision`, and the
 * build/restructure source checks the manual save shares with chat turns.
 */
const QUESTIONNAIRE_DRAFT_CONFLICT_CODES: ReadonlySet<string> = new Set<
  | typeof QUESTIONNAIRE_BUNDLE_CONFLICT_CODE
  | typeof QUESTIONNAIRE_EDIT_REVISION_CONFLICT_CODE
  | QuestionnaireBuildFailureCode
  | QuestionnaireRePlanFailureCode
>([
  QUESTIONNAIRE_BUNDLE_CONFLICT_CODE,
  QUESTIONNAIRE_EDIT_REVISION_CONFLICT_CODE,
  "build_source_conflict",
  "replan_source_conflict",
]);

export function isQuestionnaireDraftConflict(error: unknown): boolean {
  const response = conflictResponseOf(error);
  return (
    response?.status === 409 &&
    QUESTIONNAIRE_DRAFT_CONFLICT_CODES.has(response?.data?.code ?? "")
  );
}

/**
 * The one mapper for a failed write to `POST /operations` (the builder's Save,
 * and the chat's asset writes): the refusal's reviewed copy, never the
 * server's sentence, plus the questions it names: a refused operation's
 * dependents, else the question of the refusing operation (`operationIndex`),
 * else the result-draft path of an issue the batch would add.
 * `questionLabel` turns each into "Q3". A refusal names every violation of
 * the batch, so each distinct line is said once with the questions it
 * affects: the first is the title, the rest follow in the description, and
 * a way out several lines share (an asset removal's chat way) closes it,
 * once. A failure the route did not answer with a refusal reads its
 * transport line, else `fallback`: the caller's own line for what it tried.
 */
export function getSaveFailureCopy(
  error: unknown,
  context: {
    draftId?: string | null;
    /** The batch this Save sent, which a 400's path addresses. */
    operations?: Array<QuestionnaireHttpOperation>;
    questionLabel?: (ref: {
      stableKey?: string;
      operationIndex?: number;
      resultPath?: string;
    }) => string | null;
    fallback?: string;
  } = {},
): { title: string; description: string | null } {
  if (isQuestionnaireDraftConflict(error)) {
    return { title: SAVE_CONFLICT_COPY, description: null };
  }
  const body = (
    error as {
      response?: { data?: Partial<QuestionnaireOperationsErrorBody> };
    }
  )?.response?.data;
  const routeCode = body?.code;
  const first = Array.isArray(body?.errors) ? body.errors[0] : undefined;
  // The route's own refusal (409, a 400 batch) reads its rule catalog line.
  // An unreadable batch is ours: a blank text the marketer has to fill in is
  // the applier's typed refusal, named on its question (QA4 A2 #6). A route
  // whose entries are other rules' refusals (422) says those instead.
  const routeLine = routeCode ? ruleCopy(routeCode) : undefined;
  if (
    routeLine !== undefined &&
    (first === undefined || first.code === routeCode)
  ) {
    return {
      title: routeLine,
      description:
        routeCode === "operation_batch_invalid"
          ? formatReferenceLine(context.draftId)
          : null,
    };
  }
  if (first === undefined || typeof first.code !== "string") {
    // No answer, or no refusal in it: the transport line, which says to try
    // again when the Save may have landed unseen.
    const transport = transportFailureCopy(error);
    if (transport) return { title: transport.line, description: null };
    return {
      title: context.fallback ?? SAVE_FAILURE_FALLBACK,
      description: formatReferenceLine(context.draftId),
    };
  }
  // A 422 names every violation.
  const entries = (body?.errors ?? []).filter(
    (entry) => typeof entry?.code === "string",
  );
  const lines: Array<{ line: string; questions: Array<string> }> = [];
  const wayOuts = new Set<string>();
  let referenceLine: string | null = null;
  for (const entry of entries) {
    const formatted = formatIssueCopy(
      {
        code: entry.code,
        message: entry.message ?? "",
        ...(entry.params ? { params: entry.params } : {}),
      },
      context,
    );
    referenceLine ??= formatted.referenceLine;
    if (formatted.sharedWayOut) wayOuts.add(formatted.sharedWayOut);
    const ownLabels = (entry.dependents ?? []).flatMap((dependent) => {
      const label = context.questionLabel?.({
        stableKey: dependent.questionStableKey,
      });
      return label ? [label] : [];
    });
    if (ownLabels.length === 0) {
      const label =
        body?.code === "operation_result_invalid"
          ? context.questionLabel?.({ resultPath: entry.path })
          : typeof entry.operationIndex === "number"
            ? context.questionLabel?.({ operationIndex: entry.operationIndex })
            : null;
      if (label) ownLabels.push(label);
    }
    lines.push({ line: formatted.ownText, questions: ownLabels });
  }
  // One formatter for the Save's toast and the chat's reply.
  const [{ line: title, affects: firstAffects }, ...rest] =
    groupRefusalLines(lines);
  const description = [
    firstAffects,
    ...rest.map(({ line, affects }) => [line, affects].filter(Boolean).join(" ")),
    ...wayOuts,
  ]
    .filter(Boolean)
    .join(" ");
  return { title, description: description || referenceLine };
}

/**
 * Maps a questionnaire compile/validation failure to a user-facing message.
 *
 * Routes issue codes through `questionnaireIssueCopy` so the compiler 400
 * never shows the validator's engineer sentence. The floor comes from
 * `readiness.resolvedMinimum` when the caller has it, never from
 * `issue.message`.
 */
export function getCompileErrorMessage(
  error: unknown,
  fallback: string,
  context: IssueCopyContext = {},
): string {
  if (isQuestionnaireBundleConflict(error)) {
    return QUESTIONNAIRE_BUNDLE_CONFLICT_USER_MESSAGE;
  }
  if (isQuestionnaireEditRevisionConflict(error)) {
    return QUESTIONNAIRE_EDIT_REVISION_CONFLICT_USER_MESSAGE;
  }

  const errors = (
    error as {
      response?: {
        data?: {
          errors?: Array<{
            code?: string;
            message?: string;
            path?: string;
            assetId?: string;
            params?: QuestionnaireRefusalParams;
          }>;
        };
      };
    }
  )?.response?.data?.errors;

  // Prefer the count issue: it is the only compile 400 whose copy needs
  // readiness.resolvedMinimum. Otherwise take the first coded issue.
  const firstIssue = Array.isArray(errors)
    ? errors.find(
        (issue) =>
          issue?.code === "research_questions_too_few" ||
          issue?.code === "research_questions_too_many",
      ) ??
      errors.find((issue) => typeof issue?.code === "string")
    : undefined;
  if (firstIssue?.code) {
    const formatted = formatIssueCopy(
      {
        code: firstIssue.code,
        message: firstIssue.message ?? "",
        path: firstIssue.path,
        ...(firstIssue.assetId ? { assetId: firstIssue.assetId } : {}),
        ...(firstIssue.params ? { params: firstIssue.params } : {}),
      },
      context,
    );
    return formatted.referenceLine
      ? `${formatted.text} ${formatted.referenceLine}`
      : formatted.text;
  }

  return requestFailureLine(error, fallback);
}
